import { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Divider,
  Button,
  Avatar,
  Switch,
} from "@mui/material";
import {
  Receipt,
  CreditCard,
  MoneyOff,
  CardGiftcard,
  CreditCardOff,
  Money,
  ShoppingCart,
  Book,
  TrendingUp,
  ExpandMore,
  Close,
  Cancel,
  PointOfSale,
  Home,
  AddCircle,
  DarkMode,
  LightMode,
  Settings,
  Logout,
  Inventory,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";

const DRAWER_WIDTH = 280;

// Items del menú de facturación - ADMIN (acceso completo)
const billingMenuItemsAdmin = {
  "NUEVO DTE": [
    { id: "nuevo-dte", label: "Nuevo Documento", icon: <AddCircle /> },
    { id: "venta-rapida", label: "Venta Rápida", icon: <PointOfSale /> },
  ],
  PRODUCTOS: [
    { id: "gestion-productos", label: "Gestión Productos", icon: <Inventory /> },
  ],
  ANULACIONES: [
    { id: "anulacion", label: "Anular DTE", icon: <Cancel /> },
  ],
  LIBROS: [
    { id: "libros-ventas", label: "Ventas", icon: <Book /> },
    { id: "libros-compras", label: "Compras", icon: <Book /> },
  ],
  ANEXOS: [
    { id: "anexos-f07", label: "F07", icon: <TrendingUp /> },
  ],
};

// Items del menú de facturación - USER (solo emitir/consultar)
const billingMenuItemsUser = {
  "NUEVO DTE": [
    { id: "nuevo-dte", label: "Nuevo Documento", icon: <AddCircle /> },
    { id: "venta-rapida", label: "Venta Rápida", icon: <PointOfSale /> },
  ],
  PRODUCTOS: [
    { id: "gestion-productos", label: "Gestión Productos", icon: <Inventory /> },
  ],
  ANULACIONES: [
    { id: "anulacion", label: "Anular DTE", icon: <Cancel /> },
  ],
  LIBROS: [
    { id: "libros-ventas", label: "Ventas", icon: <Book /> },
    { id: "libros-compras", label: "Compras", icon: <Book /> },
  ],
  ANEXOS: [
    { id: "anexos-f07", label: "F07", icon: <TrendingUp /> },
  ],
  CONSULTAS: [
    { id: "consulta-fecha", label: "Consultar por Fecha", icon: <Book /> },
  ],
};

const BillingMenu = ({
  selectedItem,
  onSelectItem,
  mobileOpen,
  onMobileClose,
  isMobile: externalIsMobile,
}) => {
  const theme = useTheme();
  const isMobileInternal = useMediaQuery(theme.breakpoints.down("md"));
  // Usar el valor pasado externamente si está disponible, si no usar el interno
  const isMobile =
    externalIsMobile !== undefined ? externalIsMobile : isMobileInternal;
  
  // Determinar el rol del usuario en FACTURACION
  const getBillingRole = () => {
    try {
      const modulesData = localStorage.getItem('nx2026_modules');
      if (modulesData) {
        const modules = JSON.parse(modulesData);
        const billingModule = modules.find(m => 
          m.modules && m.modules.some(mod => mod.id && mod.id.toUpperCase().includes('BILLING'))
        );
        if (billingModule) {
          return billingModule.billingRole || 'user';
        }
      }
    } catch (e) {
      console.error('Error getting billing role:', e);
    }
    return 'user'; // Por defecto user
  };
  
  const billingRole = getBillingRole();
  const isAdmin = billingRole === 'admin';
  
  // Usar el menú correcto según el rol
  const menuItems = isAdmin ? billingMenuItemsAdmin : billingMenuItemsUser;
  
  const [expanded, setExpanded] = useState(() => {
    // Encontrar la sección que contiene el item seleccionado
    for (const [section, items] of Object.entries(menuItems)) {
      if (items.some((item) => item.id === selectedItem)) {
        return section;
      }
    }
    return "NUEVO DTE";
  });

  const { toggleModo } = useApp();
  const navigate = useNavigate();
  
  // Obtener estado del modo oscuro desde AppContext
  const { darkMode, toggleDarkMode } = useApp();

  // Obtener módulos autorizados desde localStorage
  const getAuthorizedModules = () => {
    try {
      const stored = localStorage.getItem('nx2026_authorized_modules');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const authorizedModules = getAuthorizedModules();
  const hasPOS = authorizedModules.includes('POS');

  // Obtener el logo de la empresa desde los módulos guardados en localStorage
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    const modulesData = localStorage.getItem('nx2026_modules');
    if (modulesData) {
      try {
        const modules = JSON.parse(modulesData);
        // Buscar la empresa que tiene el módulo de facturación (BILLING en mayúsculas)
        const billingModule = modules.find(m => 
          m.modules && m.modules.some(mod => mod.id && mod.id.toUpperCase().includes('BILLING'))
        );
        if (billingModule && billingModule.company) {
          // Priorizar urlLogo, luego logo
          const logoUrl = billingModule.company.urlLogo || billingModule.company.logo;
          if (logoUrl) {
            setCompanyLogo(logoUrl);
          }
        }
      } catch (e) {
        console.error('Error parsing modules:', e);
      }
    }
  }, []);

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpanded(isExpanded ? section : false);
  };

  const handleItemClick = (itemId) => {
    onSelectItem(itemId);
    if (isMobile) {
      onMobileClose();
    }
  };

  const handleCambiarModo = () => {
    toggleModo();
    navigate("/pos", { replace: true });
  };

  // Renderizado del contenido del menú
  const menuContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#232223",
        color: "#FFFFFF",
        "& *": {
          color: "#FFFFFF !important",
        },
      }}
    >
      {/* Header del menú */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          minHeight: 64,
        }}
      >
        {companyLogo ? (
          <Box
            sx={{
              width: "100%",
              height: 100,
              backgroundImage: `url(${companyLogo})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              borderRadius: 1,
            }}
          />
        ) : (
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#4fc3f7" }}>
            FACTURACIÓN
          </Typography>
        )}
        {isMobile && (
          <IconButton onClick={onMobileClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Secciones del menú */}
      <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
        {/* Botón Inicio - solo en desktop */}
        {!isMobile && (
          <List disablePadding sx={{ mb: 1 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate('/billing')}
                sx={{
                  py: 1.5,
                  pl: 2,
                  pr: 2,
                  mx: 1,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{ color: "rgba(255,255,255,0.7)", minWidth: 36 }}
                >
                  <Home />
                </ListItemIcon>
                <ListItemText
                  primary="Inicio"
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        )}
        {Object.entries(menuItems).map(([section, items]) => (
          <Accordion
            key={section}
            expanded={expanded === section}
            onChange={handleAccordionChange(section)}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": { display: "none" },
              "& .MuiAccordionSummary-root": {
                minHeight: 48,
                "& .MuiAccordionSummary-content": { my: 1 },
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
              }
              sx={{
                "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  letterSpacing: "0.1em",
                  color: "#4fc3f7",
                  textTransform: "uppercase",
                }}
              >
                {section}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {items.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      selected={selectedItem === item.id}
                      onClick={() => handleItemClick(item.id)}
                      sx={{
                        py: 1.5,
                        pl: 3,
                        pr: 2,
                        mx: 1,
                        borderRadius: 1,
                        "&.Mui-selected": {
                          backgroundColor: "rgba(79, 195, 247, 0.15)",
                          borderLeft: "3px solid #4fc3f7",
                          "&:hover": {
                            backgroundColor: "rgba(79, 195, 247, 0.2)",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{ color: "rgba(255,255,255,0.7)", minWidth: 36 }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: selectedItem === item.id ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Footer con información y botones de acción */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid rgba(0,0,0,0.1)",
          backgroundColor: "rgba(255,255,255,0.1)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          color: "#232223 !important",
        }}
      >
        {/* Botón DTE - solo en móvil para Facturación */}
        {isMobile && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<Description />}
            sx={{
              backgroundColor: "#ed6c02",
              color: "#ffffff !important",
              "&:hover": {
                backgroundColor: "#e65100",
              },
              textTransform: "none",
              fontWeight: "bold",
              py: 1,
            }}
          >
            DTE
          </Button>
        )}

        {/* Botón para cambiar a modo POS - solo mostrar si tiene acceso */}
        {hasPOS && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleCambiarModo}
            startIcon={<PointOfSale />}
            sx={{
              backgroundColor: "#2e7d32",
              color: "#ffffff !important",
              "&:hover": {
                backgroundColor: "#1b5e20",
              },
              textTransform: "none",
              fontWeight: "bold",
              py: 1.5,
            }}
          >
            PUNTO DE VENTA
          </Button>
        )}

        {/* Botón Configuración */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Settings />}
          onClick={() => navigate('/billing/settings')}
          sx={{
            borderColor: 'rgba(255,255,255,0.3)',
            color: '#ffffff !important',
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
            textTransform: 'none',
            py: 1,
          }}
        >
          Configuración
        </Button>

        {/* Botón Cerrar sesión */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          onClick={() => {
            localStorage.clear();
            window.location.href = '/login';
          }}
          sx={{
            borderColor: 'rgba(255,255,255,0.3)',
            color: '#ffffff !important',
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
            textTransform: 'none',
            py: 1,
          }}
        >
          Cerrar sesión
        </Button>

        {/* Switch para modo día/noche */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 1,
          }}
        >
          <LightMode sx={{ color: darkMode ? '#ffffff !important' : '#ffeb3b', fontSize: 20 }} />
          <Switch
            checked={darkMode}
            onChange={toggleDarkMode}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#4fc3f7',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#4fc3f7',
              },
            }}
          />
          <DarkMode sx={{ color: darkMode ? '#4fc3f7' : '#232223 !important', fontSize: 20 }} />
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: "#ffffff !important",
            fontWeight: 500,
          }}
        >
          Sistema de Facturación
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#ffffff !important",
            fontWeight: 500,
          }}
        >
          El Salvador v1.1.91
        </Typography>
      </Box>
    </Box>
  );

  // Renderizado para móvil (Drawer temporal)
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
            color: "white",
          },
        }}
      >
        {menuContent}
      </Drawer>
    );
  }

  // Renderizado para desktop (Drawer permanente)
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "#ffffff",
          color: "white",
          borderRight: "1px solid rgba(255,255,255,0.1)",
        },
      }}
    >
      {menuContent}
    </Drawer>
  );
};

export default BillingMenu;
export { DRAWER_WIDTH };
